/**
 * FormGenerationDialog Component
 *
 * Dialog for generating pre-filled permit application forms.
 * Features:
 * - Template selection based on jurisdiction
 * - Field preview with confidence indicators
 * - Edit fields before generation
 * - Download generated PDF
 *
 * Part of Phase 4: Form Generation
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  FileText,
  Download,
  Loader2,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Eye,
  Edit2,
  Sparkles,
  RefreshCw,
  ExternalLink,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFormGeneration } from '@/hooks/useFormGeneration';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { useJurisdictionDetail } from '@/hooks/useJurisdictions';
import { getNocThreshold } from '@/lib/notice-of-commencement';
import type { FormTemplate, ResolvedFieldValue, FormSection } from '@/types/jurisdiction';

interface FormGenerationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jurisdictionCode?: string;
  permitType?: string;
  jurisdictionId?: number | null;
  projectValue?: number;
}

type Step = 'select' | 'preview' | 'generating' | 'complete';

export function FormGenerationDialog({
  open,
  onOpenChange,
  jobId,
  jurisdictionCode,
  permitType,
  jurisdictionId,
  projectValue,
}: FormGenerationDialogProps) {
  const [step, setStep] = useState<Step>('select');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, string>>({});

  const {
    isLoading,
    isGenerating,
    error,
    result,
    previewFields,
    loadFormData,
    previewForm,
    generateForm,
    downloadForm,
    getTemplate,
    getAvailableTemplates,
    reset,
  } = useFormGeneration({ jobId });
  const { data: jurisdictionDetail } = useJurisdictionDetail(jurisdictionId ?? null);
  const nocThreshold = useMemo(() => {
    return getNocThreshold(jurisdictionDetail?.notice_of_commencement, permitType);
  }, [jurisdictionDetail?.notice_of_commencement, permitType]);
  const nocThresholdAmount = nocThreshold?.amount;
  const nocRequired = typeof projectValue === 'number'
    && typeof nocThresholdAmount === 'number'
    && projectValue >= nocThresholdAmount;

  // Get the selected template
  const selectedTemplate = useMemo(() => {
    return selectedTemplateId ? getTemplate(selectedTemplateId) : null;
  }, [selectedTemplateId, getTemplate]);

  // Load available templates when dialog opens
  const [availableTemplates, setAvailableTemplates] = useState<Awaited<
    ReturnType<typeof getAvailableTemplates>
  > | null>(null);
  const displayTemplates = useMemo(() => {
    if (!availableTemplates?.templates) return [];
    const templates = [...availableTemplates.templates];
    const nocTemplate = templates.find((template) =>
      template.formName.toLowerCase().includes('notice of commencement')
    );
    if (!nocRequired || !nocTemplate) return templates;
    return [nocTemplate, ...templates.filter((template) => template.id !== nocTemplate.id)];
  }, [availableTemplates?.templates, nocRequired]);

  useEffect(() => {
    if (open && jurisdictionCode) {
      getAvailableTemplates(jurisdictionCode).then(setAvailableTemplates);
    }
  }, [open, jurisdictionCode, getAvailableTemplates]);

  // Auto-select template if only one matches
  useEffect(() => {
    if (availableTemplates?.templates.length === 1) {
      setSelectedTemplateId(availableTemplates.templates[0].id);
    } else if (availableTemplates?.templates && permitType) {
      const matching = availableTemplates.templates.find(
        (t) => t.permitType === permitType
      );
      if (matching) {
        setSelectedTemplateId(matching.id);
      }
    }
  }, [availableTemplates, permitType]);

  // Reset state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setStep('select');
      setSelectedTemplateId(null);
      setEditMode(false);
      setFieldOverrides({});
      reset();
    }
    onOpenChange(newOpen);
  };

  // Handle template selection and preview
  const handlePreviewTemplate = async () => {
    if (!selectedTemplateId) return;

    setStep('preview');
    await loadFormData();
    await previewForm(selectedTemplateId, fieldOverrides);
  };

  // Handle field override change
  const handleFieldChange = (fieldName: string, value: string) => {
    setFieldOverrides((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  // Handle form generation
  const handleGenerate = async () => {
    if (!selectedTemplateId) return;

    setStep('generating');
    const genResult = await generateForm(selectedTemplateId, {
      overrides: fieldOverrides,
      includeWatermark: true,
      highlightMissing: true,
    });

    if (genResult?.success) {
      setStep('complete');
    } else {
      setStep('preview');
    }
  };

  // Handle download
  const handleDownload = () => {
    if (selectedTemplate) {
      const filename = `${selectedTemplate.jurisdictionCode}-${selectedTemplate.permitType}-${jobId}.pdf`;
      downloadForm(filename);
    }
  };

  // Calculate completeness
  const completeness = useMemo(() => {
    if (previewFields.length === 0) return 0;
    const filled = previewFields.filter((f) => f.value && f.value.trim() !== '').length;
    return Math.round((filled / previewFields.length) * 100);
  }, [previewFields]);

  // Group fields by section
  const fieldsBySection = useMemo(() => {
    if (!selectedTemplate) return new Map<FormSection, ResolvedFieldValue[]>();

    const map = new Map<FormSection, ResolvedFieldValue[]>();
    const fieldMap = new Map(previewFields.map((f) => [f.formField, f]));

    for (const section of selectedTemplate.sections) {
      const sectionFields = section.fields
        .map((fieldName) => fieldMap.get(fieldName))
        .filter(Boolean) as ResolvedFieldValue[];
      map.set(section, sectionFields);
    }

    return map;
  }, [selectedTemplate, previewFields]);

  // Render confidence indicator
  const renderConfidence = (field: ResolvedFieldValue) => {
    const confidenceMap = {
      high: 'verified',
      medium: 'medium',
      low: 'low',
    } as const;

    return (
      <ConfidenceBadge
        level={confidenceMap[field.confidence]}
        size="sm"
        showLabel={false}
      />
    );
  };

  // Render field input based on type
  const renderFieldInput = (field: ResolvedFieldValue) => {
    const mapping = selectedTemplate?.fieldMappings.find(
      (m) => m.formField === field.formField
    );
    const value = fieldOverrides[field.formField] ?? field.value;

    if (!editMode) {
      return (
        <div className="flex items-center gap-2">
          <span className={cn('text-sm', !value && 'text-muted-foreground italic')}>
            {value || 'Not provided'}
          </span>
          {renderConfidence(field)}
        </div>
      );
    }

    const handleChange = (newValue: string) => handleFieldChange(field.formField, newValue);

    switch (mapping?.fieldType) {
      case 'textarea':
        return (
          <Textarea
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={mapping.label}
            rows={3}
            maxLength={mapping.maxLength}
          />
        );

      case 'select':
        return (
          <Select value={value} onValueChange={handleChange}>
            <SelectTrigger>
              <SelectValue placeholder={`Select ${mapping.label}`} />
            </SelectTrigger>
            <SelectContent>
              {mapping.options?.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
          />
        );

      case 'checkbox':
        return (
          <Select value={value} onValueChange={handleChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
            </SelectContent>
          </Select>
        );

      default:
        return (
          <Input
            type="text"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={mapping?.label}
            maxLength={mapping?.maxLength}
          />
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Generate Permit Application
          </DialogTitle>
          <DialogDescription>
            {step === 'select' && 'Select a form template to generate a pre-filled permit application.'}
            {step === 'preview' && 'Review and edit the form fields before generating your application.'}
            {step === 'generating' && 'Generating your permit application...'}
            {step === 'complete' && 'Your permit application is ready to download!'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden py-4">
          {/* Step 1: Template Selection */}
          {step === 'select' && (
            <div className="space-y-4">
              {!availableTemplates?.hasTemplates ? (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>Templates Coming Soon</AlertTitle>
                  <AlertDescription>
                    {availableTemplates?.noTemplatesMessage ||
                      'Form templates for this jurisdiction are not yet available.'}
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Select Form Template</Label>
                    <Select
                      value={selectedTemplateId || ''}
                      onValueChange={setSelectedTemplateId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a template..." />
                      </SelectTrigger>
                      <SelectContent>
                        {displayTemplates.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            <div className="flex items-center gap-2">
                              <span>{t.formName}</span>
                              <Badge variant="outline" className="text-xs">
                                {t.permitType}
                              </Badge>
                              {nocRequired && t.formName.toLowerCase().includes('notice of commencement') && (
                                <Badge variant="secondary" className="text-xs">
                                  Required
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedTemplate && (
                    <div className="p-4 bg-muted rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">{selectedTemplate.formName}</h4>
                        <Badge variant="secondary">v{selectedTemplate.version}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {selectedTemplate.notes}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Updated: {selectedTemplate.lastUpdated}</span>
                        {selectedTemplate.sourceUrl && (
                          <a
                            href={selectedTemplate.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            Original Form <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 2: Preview & Edit */}
          {step === 'preview' && selectedTemplate && (
            <div className="space-y-4">
              {/* Progress and Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">Completeness</span>
                      <span className="text-sm text-muted-foreground">{completeness}%</span>
                    </div>
                    <Progress value={completeness} className="h-2 w-32" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={editMode ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEditMode(!editMode)}
                  >
                    {editMode ? (
                      <>
                        <Eye className="h-4 w-4 mr-1" />
                        Preview
                      </>
                    ) : (
                      <>
                        <Edit2 className="h-4 w-4 mr-1" />
                        Edit
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => previewForm(selectedTemplateId!, fieldOverrides)}
                    disabled={isLoading}
                  >
                    <RefreshCw className={cn('h-4 w-4 mr-1', isLoading && 'animate-spin')} />
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Field Sections */}
              <ScrollArea className="h-[400px] border rounded-lg">
                <Accordion
                  type="multiple"
                  defaultValue={selectedTemplate.sections
                    .filter((s) => !s.defaultCollapsed)
                    .map((s) => s.id)}
                  className="p-4"
                >
                  {selectedTemplate.sections.map((section) => {
                    const sectionFields = fieldsBySection.get(section) || [];
                    const filledCount = sectionFields.filter(
                      (f) => f.value && f.value.trim() !== ''
                    ).length;

                    return (
                      <AccordionItem key={section.id} value={section.id}>
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3">
                            <span>{section.title}</span>
                            <Badge
                              variant={
                                filledCount === sectionFields.length
                                  ? 'default'
                                  : filledCount > 0
                                  ? 'secondary'
                                  : 'outline'
                              }
                              className="text-xs"
                            >
                              {filledCount}/{sectionFields.length}
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          {section.description && (
                            <p className="text-sm text-muted-foreground mb-3">
                              {section.description}
                            </p>
                          )}
                          <div className="grid gap-3">
                            {sectionFields.map((field) => {
                              const mapping = selectedTemplate.fieldMappings.find(
                                (m) => m.formField === field.formField
                              );

                              return (
                                <div
                                  key={field.formField}
                                  className={cn(
                                    'grid grid-cols-[180px_1fr] gap-3 items-start p-2 rounded',
                                    !field.isValid && 'bg-destructive/5'
                                  )}
                                >
                                  <Label className="text-sm pt-2 flex items-center gap-1">
                                    {mapping?.label || field.formField}
                                    {mapping?.required && (
                                      <span className="text-destructive">*</span>
                                    )}
                                  </Label>
                                  <div>
                                    {renderFieldInput(field)}
                                    {field.source !== 'user_override' && field.value && (
                                      <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                                        <Sparkles className="h-3 w-3" />
                                        Auto-filled from {field.source.replace('_', ' ')}
                                      </div>
                                    )}
                                    {!field.isValid && (
                                      <p className="text-xs text-destructive mt-1">
                                        {field.validationError}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </ScrollArea>

              {/* Warnings */}
              {previewFields.filter((f) => !f.isValid).length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Missing Required Fields</AlertTitle>
                  <AlertDescription>
                    {previewFields.filter((f) => !f.isValid).length} required field(s) are missing.
                    The form can still be generated, but may not be accepted.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Step 3: Generating */}
          {step === 'generating' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <h3 className="text-lg font-medium mb-2">Generating Your Application</h3>
              <p className="text-muted-foreground text-center max-w-md">
                We're filling in your permit application form with the data you provided.
                This should only take a moment.
              </p>
            </div>
          )}

          {/* Step 4: Complete */}
          {step === 'complete' && result && (
            <div className="space-y-6">
              <div className="flex flex-col items-center py-6">
                <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-medium mb-2">Application Ready!</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  Your permit application has been generated and is ready to download.
                </p>
              </div>

              {/* Summary */}
              <div className="bg-muted rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Form Completeness</span>
                  <div className="flex items-center gap-2">
                    <Progress value={result.completeness} className="h-2 w-24" />
                    <span className="text-sm">{result.completeness}%</span>
                  </div>
                </div>

                {result.fieldsNeedingReview.length > 0 && (
                  <Alert className="bg-amber-50 dark:bg-amber-900/20 border-amber-200">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertTitle className="text-amber-800 dark:text-amber-200">
                      Review Recommended
                    </AlertTitle>
                    <AlertDescription className="text-amber-700 dark:text-amber-300">
                      {result.fieldsNeedingReview.length} field(s) may need verification before submission.
                    </AlertDescription>
                  </Alert>
                )}

                {result.warnings.map((warning, i) => (
                  <Alert key={i} variant="default">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{warning}</AlertDescription>
                  </Alert>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          {step === 'select' && (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handlePreviewTemplate}
                disabled={!selectedTemplateId || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Preview Form
                  </>
                )}
              </Button>
            </>
          )}

          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('select')}>
                Back
              </Button>
              <Button onClick={handleGenerate} disabled={isGenerating}>
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Generate PDF
                  </>
                )}
              </Button>
            </>
          )}

          {step === 'complete' && (
            <>
              <Button variant="outline" onClick={() => setStep('preview')}>
                Edit & Regenerate
              </Button>
              <Button onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FormGenerationDialog;
